import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Briefcase, Users, Plus, Edit, Trash2, Eye, Archive, 
  MapPin, Clock, DollarSign, CheckCircle, XCircle, 
  Calendar, FileText, Mail, Phone, User, ExternalLink,
  Building, ChevronDown, ChevronUp, Search, Filter, X, Award, Printer
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import api from '../../services/api';
import DOMPurify from 'dompurify';
import MobileActionBar from '../common/MobileActionBar';

const EMPLOYMENT_TYPES = [
  { value: 'full_time', label: 'Full Time' },
  { value: 'part_time', label: 'Part Time' },
  { value: 'contract', label: 'Contract' },
  { value: 'temporary', label: 'Temporary' },
  { value: 'internship', label: 'Internship' }
];

const POSTING_STATUSES = [
  { value: 'draft', label: 'Draft', color: 'bg-[var(--background-subtle)] text-[var(--text-primary)]' },
  { value: 'active', label: 'Active', color: 'bg-[var(--success)]/15 text-green-700' },
  { value: 'paused', label: 'Paused', color: 'bg-[var(--warning)]/15 text-yellow-700' },
  { value: 'filled', label: 'Filled', color: 'bg-[#D4AF37]/10 text-[#D4AF37]' },
  { value: 'closed', label: 'Closed', color: 'bg-[var(--error)]/15 text-red-700' },
  { value: 'archived', label: 'Archived', color: 'bg-slate-100 text-slate-700' }
];

const APPLICATION_STATUSES = [
  { value: 'submitted', label: 'New', color: 'bg-[#D4AF37]/10 text-[#D4AF37]' },
  { value: 'under_review', label: 'Under Review', color: 'bg-[var(--warning)]/15 text-yellow-700' },
  { value: 'interview_scheduled', label: 'Interview Scheduled', color: 'bg-[#F4C2C2]/15 text-purple-700' },
  { value: 'interviewed', label: 'Interviewed', color: 'bg-indigo-100 text-indigo-700' },
  { value: 'offer_extended', label: 'Offer Extended', color: 'bg-teal-100 text-teal-700' },
  { value: 'hired', label: 'Hired', color: 'bg-[var(--success)]/15 text-green-700' },
  { value: 'rejected', label: 'Rejected', color: 'bg-[var(--error)]/15 text-red-700' },
  { value: 'withdrawn', label: 'Withdrawn', color: 'bg-[var(--background-subtle)] text-[var(--text-primary)]' },
  { value: 'archived', label: 'Archived', color: 'bg-slate-100 text-slate-700' }
];

const DEPARTMENTS = [
  'Management',
  'Scheduling',
  'Support Staff',
  'Delivery Services',
  'Private Concierge',
  'Social Engagement',
  'Courier/Delivery',
  'Errands',
  'Administration',
  'Other'
];

const CareersTab = () => {
  const [activeView, setActiveView] = useState('postings'); // postings | applications
  const [postings, setPostings] = useState([]);
  const [applications, setApplications] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modals
  const [showPostingModal, setShowPostingModal] = useState(false);
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const [selectedPosting, setSelectedPosting] = useState(null);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  
  // Form data
  const [postingForm, setPostingForm] = useState({
    title: '',
    department: 'Delivery Services',
    employment_type: 'full_time',
    location: 'Tarboro, NC',
    is_remote: false,
    description: '',
    responsibilities: [''],
    requirements: [''],
    preferred_qualifications: [''],
    benefits: [''],
    salary_min: '',
    salary_max: '',
    salary_type: 'hourly',
    status: 'draft',
    create_badge: true,
    badge_color: 'blue'
  });

  useEffect(() => {
    fetchData();
  }, [activeView, filter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes] = await Promise.all([
        api.get('/careers/stats')
      ]);
      setStats(statsRes.data);
      
      if (activeView === 'postings') {
        const params = filter ? `?status=${filter}` : '';
        const res = await api.get(`/careers${params}`);
        setPostings(res.data);
      } else {
        const params = filter ? `?status=${filter}` : '';
        const res = await api.get(`/careers/applications/all${params}`);
        setApplications(res.data);
      }
    } catch (error) {
      console.error('Failed to fetch careers data:', error);
      toast.error('Failed to load careers data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePosting = async () => {
    if (!postingForm.title || !postingForm.description) {
      toast.error('Please fill in title and description');
      return;
    }
    
    try {
      const payload = {
        ...postingForm,
        salary_min: postingForm.salary_min ? parseFloat(postingForm.salary_min) : null,
        salary_max: postingForm.salary_max ? parseFloat(postingForm.salary_max) : null,
        responsibilities: postingForm.responsibilities.filter(r => r.trim()),
        requirements: postingForm.requirements.filter(r => r.trim()),
        preferred_qualifications: postingForm.preferred_qualifications.filter(r => r.trim()),
        benefits: postingForm.benefits.filter(r => r.trim())
      };
      
      if (isEditing && selectedPosting) {
        await api.put(`/careers/${selectedPosting.id}`, payload);
        toast.success('Job posting updated!');
      } else {
        await api.post('/careers', payload);
        toast.success('Job posting created!');
      }
      
      setShowPostingModal(false);
      resetPostingForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save posting');
    }
  };

  const handleDeletePosting = async (postingId) => {
    if (!window.confirm('Archive this job posting?')) return;
    try {
      await api.delete(`/careers/${postingId}`);
      toast.success('Job posting archived');
      fetchData();
    } catch (error) {
      toast.error('Failed to archive posting');
    }
  };

  // Print job posting
  const handlePrintPosting = (posting) => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${DOMPurify.sanitize(posting.title || '')} - NC Help Hub Careers</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
          .header { text-align: center; border-bottom: 2px solid #D4AF37; padding-bottom: 20px; margin-bottom: 30px; }
          .header h1 { color: #D4AF37; margin: 0 0 10px 0; }
          .header h2 { margin: 0; color: #333; }
          .meta { display: flex; flex-wrap: wrap; gap: 20px; margin-bottom: 20px; }
          .meta-item { display: flex; align-items: center; gap: 8px; color: #666; }
          .section { margin-bottom: 25px; }
          .section h3 { color: #D4AF37; border-bottom: 1px solid #eee; padding-bottom: 8px; }
          .requirements ul { padding-left: 20px; }
          .requirements li { margin-bottom: 8px; }
          .benefits { display: flex; flex-wrap: wrap; gap: 10px; }
          .benefit { background: #fdf2f8; padding: 5px 15px; border-radius: 20px; font-size: 14px; }
          .footer { text-align: center; margin-top: 40px; padding-top: 20px; border-top: 1px solid #eee; color: #888; }
          .apply-info { background: #f0fdf4; padding: 20px; border-radius: 8px; text-align: center; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>NC Help Hub</h1>
          <h2>${DOMPurify.sanitize(posting.title || '')}</h2>
          <p style="color: #888;">${DOMPurify.sanitize(posting.department || 'General')}</p>
        </div>
        <div class="meta">
          <div class="meta-item">📍 ${DOMPurify.sanitize(posting.location || 'Tarboro, NC')}</div>
          <div class="meta-item">⏰ ${(posting.employment_type || 'full_time').replace('_', ' ').toUpperCase()}</div>
          <div class="meta-item">💰 ${DOMPurify.sanitize(posting.salary_range || 'Competitive')}</div>
          <div class="meta-item">📅 Posted: ${new Date(posting.created_at).toLocaleDateString()}</div>
        </div>
        <div class="section">
          <h3>Job Description</h3>
          <p>${DOMPurify.sanitize(posting.description || 'No description provided.')}</p>
        </div>
        ${posting.requirements?.length ? `
        <div class="section requirements">
          <h3>Requirements</h3>
          <ul>
            ${posting.requirements.map(r => `<li>${DOMPurify.sanitize(r)}</li>`).join('')}
          </ul>
        </div>
        ` : ''}
        ${posting.benefits?.length ? `
        <div class="section">
          <h3>Benefits</h3>
          <div class="benefits">
            ${posting.benefits.map(b => `<span class="benefit">✓ ${DOMPurify.sanitize(b)}</span>`).join('')}
          </div>
        </div>
        ` : ''}
        <div class="apply-info">
          <h3>Interested in this position?</h3>
          <p>Apply online at <strong>nchelphub.com/careers</strong></p>
          <p>or contact us at <strong>careers@nchelphub.com</strong></p>
        </div>
        <div class="footer">
          <p>NC Help Hub - Premium Home Services</p>
          <p>Printed: ${new Date().toLocaleString()}</p>
        </div>
      </body>
      </html>
    `;
    printWindow.document.open();
    printWindow.document.write(DOMPurify.sanitize(printContent, { WHOLE_DOCUMENT: true }));
    printWindow.document.close();
    printWindow.print();
  };

  // Print application
  const handlePrintApplication = (application) => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Application - ${DOMPurify.sanitize(application.first_name || '')} ${DOMPurify.sanitize(application.last_name || '')}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
          .header { text-align: center; border-bottom: 2px solid #D4AF37; padding-bottom: 20px; margin-bottom: 30px; }
          .header h1 { color: #D4AF37; margin: 0 0 10px 0; }
          .applicant-name { font-size: 24px; font-weight: bold; margin: 10px 0; }
          .section { margin-bottom: 25px; }
          .section h3 { color: #D4AF37; border-bottom: 1px solid #eee; padding-bottom: 8px; }
          .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; }
          .info-item label { font-weight: bold; color: #666; display: block; font-size: 12px; }
          .info-item span { font-size: 16px; }
          .status { display: inline-block; padding: 5px 15px; border-radius: 20px; font-weight: bold; }
          .status.pending { background: #fef3c7; color: #92400e; }
          .status.approved { background: #d1fae5; color: #065f46; }
          .status.rejected { background: #fee2e2; color: #991b1b; }
          .footer { text-align: center; margin-top: 40px; color: #888; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>NC Help Hub</h1>
          <p>Job Application</p>
          <div class="applicant-name">${application.first_name} ${application.last_name}</div>
          <span class="status ${application.status}">${application.status?.toUpperCase()}</span>
        </div>
        <div class="section">
          <h3>Contact Information</h3>
          <div class="info-grid">
            <div class="info-item"><label>Email</label><span>${application.email}</span></div>
            <div class="info-item"><label>Phone</label><span>${application.phone || 'Not provided'}</span></div>
            <div class="info-item"><label>Address</label><span>${application.address || 'Not provided'}</span></div>
            <div class="info-item"><label>Applied</label><span>${new Date(application.created_at).toLocaleDateString()}</span></div>
          </div>
        </div>
        <div class="section">
          <h3>Position Details</h3>
          <div class="info-grid">
            <div class="info-item"><label>Position</label><span>${application.position || application.job_title || 'General Application'}</span></div>
            <div class="info-item"><label>Desired Start</label><span>${application.available_start_date || 'Immediately'}</span></div>
          </div>
        </div>
        ${application.experience ? `
        <div class="section">
          <h3>Experience</h3>
          <p>${application.experience}</p>
        </div>
        ` : ''}
        ${application.cover_letter ? `
        <div class="section">
          <h3>Cover Letter</h3>
          <p>${application.cover_letter}</p>
        </div>
        ` : ''}
        <div class="footer">
          <p>Application ID: ${DOMPurify.sanitize(application.id || '')}</p>
          <p>Printed: ${new Date().toLocaleString()}</p>
        </div>
      </body>
      </html>
    `;
    printWindow.document.open();
    printWindow.document.write(DOMPurify.sanitize(printContent, { WHOLE_DOCUMENT: true }));
    printWindow.document.close();
    printWindow.print();
  };

  const handleUpdatePostingStatus = async (postingId, newStatus) => {
    try {
      await api.put(`/careers/${postingId}`, { status: newStatus });
      toast.success('Status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleUpdateApplicationStatus = async (applicationId, newStatus, notes = null) => {
    try {
      const params = new URLSearchParams({ status: newStatus });
      if (notes) params.append('notes', notes);
      await api.put(`/careers/applications/${applicationId}/status?${params.toString()}`);
      toast.success('Application status updated');
      fetchData();
      if (selectedApplication?.id === applicationId) {
        setSelectedApplication(prev => ({ ...prev, status: newStatus }));
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleArchiveApplication = async (applicationId) => {
    try {
      await api.delete(`/careers/applications/${applicationId}`);
      toast.success('Application archived');
      fetchData();
      setShowApplicationModal(false);
    } catch (error) {
      toast.error('Failed to archive application');
    }
  };

  const resetPostingForm = () => {
    setPostingForm({
      title: '',
      department: 'Delivery Services',
      employment_type: 'full_time',
      location: 'Tarboro, NC',
      is_remote: false,
      description: '',
      responsibilities: [''],
      requirements: [''],
      preferred_qualifications: [''],
      benefits: [''],
      salary_min: '',
      salary_max: '',
      salary_type: 'hourly',
      status: 'draft',
      create_badge: true,
      badge_color: 'blue'
    });
    setIsEditing(false);
    setSelectedPosting(null);
  };

  const openEditPosting = (posting) => {
    setPostingForm({
      title: posting.title,
      department: posting.department,
      employment_type: posting.employment_type,
      location: posting.location,
      is_remote: posting.is_remote,
      description: posting.description,
      responsibilities: posting.responsibilities?.length ? posting.responsibilities : [''],
      requirements: posting.requirements?.length ? posting.requirements : [''],
      preferred_qualifications: posting.preferred_qualifications?.length ? posting.preferred_qualifications : [''],
      benefits: posting.benefits?.length ? posting.benefits : [''],
      salary_min: posting.salary_min?.toString() || '',
      salary_max: posting.salary_max?.toString() || '',
      salary_type: posting.salary_type || 'hourly',
      status: posting.status
    });
    setSelectedPosting(posting);
    setIsEditing(true);
    setShowPostingModal(true);
  };

  const addListItem = (field) => {
    setPostingForm(prev => ({
      ...prev,
      [field]: [...prev[field], '']
    }));
  };

  const updateListItem = (field, index, value) => {
    setPostingForm(prev => ({
      ...prev,
      [field]: prev[field].map((item, i) => i === index ? value : item)
    }));
  };

  const removeListItem = (field, index) => {
    setPostingForm(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const getStatusBadge = (status, type = 'posting') => {
    const statuses = type === 'posting' ? POSTING_STATUSES : APPLICATION_STATUSES;
    const s = statuses.find(st => st.value === status);
    if (!s) return <span className="px-2 py-1 rounded-full text-xs bg-[var(--background-subtle)]">{status}</span>;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${s.color}`}>
        {s.label}
      </span>
    );
  };

  const filteredPostings = postings.filter(p => 
    p.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.department.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredApplications = applications.filter(a =>
    `${a.first_name} ${a.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (a.job_title || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading && !stats) {
    return (
      <div className="p-12 text-center">
        <div className="animate-spin w-8 h-8 border-2 border-[var(--primary)] border-t-transparent rounded-full mx-auto mb-4"></div>
        <p className="text-[var(--text-muted)]">Loading careers data...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-2xl font-bold font-['Cormorant_Garamond'] flex items-center gap-2">
            <Briefcase className="w-6 h-6 text-[var(--primary)]" />
            Careers & Hiring
          </h2>
          <p className="text-[var(--text-muted)] text-sm mt-1">Manage job postings and review applications</p>
        </div>
        <Button 
          onClick={() => { resetPostingForm(); setShowPostingModal(true); }} 
          className="btn-primary"
          data-testid="create-job-posting-btn"
        >
          <Plus className="w-4 h-4 mr-2" /> Create Job Posting
        </Button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="card p-4 text-center border-2 border-[var(--success)]/30 bg-[var(--success)]/10">
            <p className="text-3xl font-bold text-[var(--success)]">{stats.active_postings}</p>
            <p className="text-sm text-green-700">Active Postings</p>
          </div>
          <div className="card p-4 text-center border-2 border-[#D4AF37]/30 bg-[#D4AF37]/10">
            <p className="text-3xl font-bold text-[#D4AF37]">{stats.new_applications}</p>
            <p className="text-sm text-[#D4AF37]">New Applications</p>
          </div>
          <div className="card p-4 text-center">
            <p className="text-3xl font-bold text-[var(--warning)]">{stats.under_review}</p>
            <p className="text-sm text-[var(--text-muted)]">Under Review</p>
          </div>
          <div className="card p-4 text-center">
            <p className="text-3xl font-bold text-teal-600">{stats.hired}</p>
            <p className="text-sm text-[var(--text-muted)]">Hired</p>
          </div>
        </div>
      )}

      {/* View Toggle */}
      <div className="flex gap-2 border-b">
        <button
          onClick={() => { setActiveView('postings'); setFilter(''); }}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            activeView === 'postings' 
              ? 'border-[var(--primary)] text-[var(--primary)]' 
              : 'border-transparent text-[var(--text-muted)] hover:text-[var(--text)]'
          }`}
          data-testid="postings-tab"
        >
          <Briefcase className="w-4 h-4 inline mr-2" />
          Job Postings ({stats?.total_postings || 0})
        </button>
        <button
          onClick={() => { setActiveView('applications'); setFilter(''); }}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            activeView === 'applications' 
              ? 'border-[var(--primary)] text-[var(--primary)]' 
              : 'border-transparent text-[var(--text-muted)] hover:text-[var(--text)]'
          }`}
          data-testid="applications-tab"
        >
          <Users className="w-4 h-4 inline mr-2" />
          Applications ({stats?.total_applications || 0})
        </button>
      </div>

      {/* Search and Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder={activeView === 'postings' ? 'Search postings...' : 'Search applications...'}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button
            variant={filter === '' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter('')}
          >
            All
          </Button>
          {(activeView === 'postings' ? POSTING_STATUSES : APPLICATION_STATUSES).slice(0, 5).map(s => (
            <Button
              key={s.value}
              variant={filter === s.value ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter(s.value)}
            >
              {s.label}
            </Button>
          ))}
        </div>
      </div>

      {/* Content */}
      {activeView === 'postings' ? (
        <div className="space-y-4">
          {filteredPostings.length === 0 ? (
            <div className="card p-12 text-center">
              <Briefcase className="w-16 h-16 text-[var(--text-muted)] mx-auto mb-4" />
              <h3 className="text-lg font-bold mb-2">No Job Postings</h3>
              <p className="text-[var(--text-muted)] mb-4">Create your first job posting to start hiring.</p>
              <Button onClick={() => { resetPostingForm(); setShowPostingModal(true); }} className="btn-primary">
                <Plus className="w-4 h-4 mr-2" /> Create Job Posting
              </Button>
            </div>
          ) : (
            filteredPostings.map((posting) => (
              <motion.div
                key={posting.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="card p-5"
                data-testid={`posting-${posting.id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      {getStatusBadge(posting.status)}
                      <span className="text-xs text-[var(--text-muted)]">
                        {posting.application_count || 0} applicant{posting.application_count !== 1 ? 's' : ''}
                      </span>
                      {posting.badge_name && (
                        <span className={`text-xs px-2 py-0.5 rounded-full bg-${posting.badge_color || 'blue'}-100 text-${posting.badge_color || 'blue'}-700 flex items-center gap-1`}>
                          <Award className="w-3 h-3" />
                          Badge: {posting.badge_name}
                        </span>
                      )}
                    </div>
                    <h3 className="font-bold text-lg">{posting.title}</h3>
                    <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-[var(--text-muted)]">
                      <span className="flex items-center gap-1">
                        <Building className="w-3 h-3" />
                        {posting.department}
                      </span>
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {posting.location}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {EMPLOYMENT_TYPES.find(t => t.value === posting.employment_type)?.label}
                      </span>
                      {(posting.salary_min || posting.salary_max) && (
                        <span className="flex items-center gap-1">
                          <DollarSign className="w-3 h-3" />
                          {posting.salary_min && posting.salary_max 
                            ? `$${posting.salary_min} - $${posting.salary_max}/${posting.salary_type === 'hourly' ? 'hr' : 'yr'}`
                            : posting.salary_min 
                              ? `From $${posting.salary_min}/${posting.salary_type === 'hourly' ? 'hr' : 'yr'}`
                              : `Up to $${posting.salary_max}/${posting.salary_type === 'hourly' ? 'hr' : 'yr'}`
                          }
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-[var(--text-muted)] line-clamp-2 mt-2">{posting.description}</p>
                  </div>
                  <div className="flex flex-col gap-2">
                    {posting.status === 'draft' && (
                      <Button
                        size="sm"
                        onClick={() => handleUpdatePostingStatus(posting.id, 'active')}
                        className="btn-primary"
                      >
                        Publish
                      </Button>
                    )}
                    {posting.status === 'active' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleUpdatePostingStatus(posting.id, 'paused')}
                      >
                        Pause
                      </Button>
                    )}
                    {posting.status === 'paused' && (
                      <Button
                        size="sm"
                        onClick={() => handleUpdatePostingStatus(posting.id, 'active')}
                        className="btn-primary"
                      >
                        Resume
                      </Button>
                    )}
                    <div className="flex items-center gap-1">
                      <Button size="sm" variant="ghost" onClick={() => openEditPosting(posting)} title="Edit">
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="text-[var(--text-secondary)]"
                        onClick={(e) => { e.stopPropagation(); handlePrintPosting(posting); }}
                        title="Print"
                      >
                        <Printer className="w-4 h-4" />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="text-[var(--error)]"
                        onClick={() => handleDeletePosting(posting.id)} 
                        title="Archive"
                      >
                        <Archive className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {filteredApplications.length === 0 ? (
            <div className="card p-12 text-center">
              <Users className="w-16 h-16 text-[var(--text-muted)] mx-auto mb-4" />
              <h3 className="text-lg font-bold mb-2">No Applications</h3>
              <p className="text-[var(--text-muted)]">Applications will appear here when candidates apply.</p>
            </div>
          ) : (
            filteredApplications.map((application) => (
              <motion.div
                key={application.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="card p-5 cursor-pointer hover:shadow-md transition-shadow"
                onClick={() => { setSelectedApplication(application); setShowApplicationModal(true); }}
                data-testid={`application-${application.id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      {getStatusBadge(application.status, 'application')}
                      <span className="text-xs text-[var(--text-muted)]">
                        Applied {new Date(application.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <h3 className="font-bold text-lg">{application.first_name} {application.last_name}</h3>
                    <p className="text-sm text-[var(--text-muted)]">Applied for: {application.job_title || 'Unknown Position'}</p>
                    <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-[var(--text-muted)]">
                      <span className="flex items-center gap-1">
                        <Mail className="w-3 h-3" />
                        {application.email}
                      </span>
                      <span className="flex items-center gap-1">
                        <Phone className="w-3 h-3" />
                        {application.phone}
                      </span>
                      {application.city && (
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {application.city}, {application.state}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); }}>
                      <Eye className="w-4 h-4 mr-1" /> View
                    </Button>
                  </div>
                </div>
              </motion.div>
            ))
          )}
        </div>
      )}

      {/* Job Posting Modal */}
      <AnimatePresence>
        {showPostingModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowPostingModal(false)}>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-[var(--background-paper)] rounded-xl w-full max-w-3xl max-h-[90vh] overflow-hidden"
              onClick={e => e.stopPropagation()}
            >
              <div className="sticky top-0 bg-[var(--background-paper)] border-b p-4 flex items-center justify-between z-10">
                <h3 className="text-xl font-bold">{isEditing ? 'Edit Job Posting' : 'Create Job Posting'}</h3>
                <Button variant="ghost" size="icon" onClick={() => setShowPostingModal(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>
              <div className="p-6 space-y-6 overflow-y-auto max-h-[calc(90vh-130px)]">
                {/* Basic Info */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label>Job Title *</Label>
                    <Input
                      value={postingForm.title}
                      onChange={e => setPostingForm({...postingForm, title: e.target.value})}
                      placeholder="e.g., Scheduler / Dispatch Coordinator"
                    />
                  </div>
                  <div>
                    <Label>Department *</Label>
                    <Select value={postingForm.department} onValueChange={v => setPostingForm({...postingForm, department: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {DEPARTMENTS.map(d => (
                          <SelectItem key={d} value={d}>{d}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <Label>Employment Type</Label>
                    <Select value={postingForm.employment_type} onValueChange={v => setPostingForm({...postingForm, employment_type: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {EMPLOYMENT_TYPES.map(t => (
                          <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Location</Label>
                    <Input
                      value={postingForm.location}
                      onChange={e => setPostingForm({...postingForm, location: e.target.value})}
                      placeholder="Tarboro, NC"
                    />
                  </div>
                  <div>
                    <Label>Status</Label>
                    <Select value={postingForm.status} onValueChange={v => setPostingForm({...postingForm, status: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {POSTING_STATUSES.slice(0, 4).map(s => (
                          <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Salary */}
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <Label>Salary Min ($)</Label>
                    <Input
                      type="number"
                      value={postingForm.salary_min}
                      onChange={e => setPostingForm({...postingForm, salary_min: e.target.value})}
                      placeholder="15"
                    />
                  </div>
                  <div>
                    <Label>Salary Max ($)</Label>
                    <Input
                      type="number"
                      value={postingForm.salary_max}
                      onChange={e => setPostingForm({...postingForm, salary_max: e.target.value})}
                      placeholder="25"
                    />
                  </div>
                  <div>
                    <Label>Salary Type</Label>
                    <Select value={postingForm.salary_type} onValueChange={v => setPostingForm({...postingForm, salary_type: v})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="hourly">Hourly</SelectItem>
                        <SelectItem value="yearly">Yearly</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Description */}
                <div>
                  <Label>Job Description *</Label>
                  <Textarea
                    value={postingForm.description}
                    onChange={e => setPostingForm({...postingForm, description: e.target.value})}
                    placeholder="Describe the role, day-to-day responsibilities, and what makes this position unique..."
                    rows={4}
                  />
                </div>

                {/* Responsibilities */}
                <div>
                  <Label className="flex items-center justify-between">
                    Responsibilities
                    <Button type="button" variant="ghost" size="sm" onClick={() => addListItem('responsibilities')}>
                      <Plus className="w-3 h-3 mr-1" /> Add
                    </Button>
                  </Label>
                  {postingForm.responsibilities.map((item, idx) => (
                    <div key={`responsibility-${idx}-${item.slice(0,10)}`} className="flex gap-2 mt-2">
                      <Input
                        value={item}
                        onChange={e => updateListItem('responsibilities', idx, e.target.value)}
                        placeholder="e.g., Coordinate daily schedules"
                      />
                      {postingForm.responsibilities.length > 1 && (
                        <Button type="button" variant="ghost" size="icon" onClick={() => removeListItem('responsibilities', idx)}>
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>

                {/* Requirements */}
                <div>
                  <Label className="flex items-center justify-between">
                    Requirements
                    <Button type="button" variant="ghost" size="sm" onClick={() => addListItem('requirements')}>
                      <Plus className="w-3 h-3 mr-1" /> Add
                    </Button>
                  </Label>
                  {postingForm.requirements.map((item, idx) => (
                    <div key={`requirement-${idx}-${item.slice(0,10)}`} className="flex gap-2 mt-2">
                      <Input
                        value={item}
                        onChange={e => updateListItem('requirements', idx, e.target.value)}
                        placeholder="e.g., 2+ years scheduling experience"
                      />
                      {postingForm.requirements.length > 1 && (
                        <Button type="button" variant="ghost" size="icon" onClick={() => removeListItem('requirements', idx)}>
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>

                {/* Benefits */}
                <div>
                  <Label className="flex items-center justify-between">
                    Benefits
                    <Button type="button" variant="ghost" size="sm" onClick={() => addListItem('benefits')}>
                      <Plus className="w-3 h-3 mr-1" /> Add
                    </Button>
                  </Label>
                  {postingForm.benefits.map((item, idx) => (
                    <div key={`benefit-${idx}-${item.slice(0,10)}`} className="flex gap-2 mt-2">
                      <Input
                        value={item}
                        onChange={e => updateListItem('benefits', idx, e.target.value)}
                        placeholder="e.g., Flexible hours"
                      />
                      {postingForm.benefits.length > 1 && (
                        <Button type="button" variant="ghost" size="icon" onClick={() => removeListItem('benefits', idx)}>
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="is_remote"
                    checked={postingForm.is_remote}
                    onChange={e => setPostingForm({...postingForm, is_remote: e.target.checked})}
                    className="w-4 h-4"
                  />
                  <label htmlFor="is_remote" className="text-sm">This position allows remote work</label>
                </div>

                {/* Badge Auto-Creation */}
                <div className="p-4 bg-[var(--accent)] rounded-lg space-y-3">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="create_badge"
                      checked={postingForm.create_badge}
                      onChange={e => setPostingForm({...postingForm, create_badge: e.target.checked})}
                      className="w-4 h-4"
                    />
                    <label htmlFor="create_badge" className="text-sm font-medium flex items-center gap-2">
                      <Award className="w-4 h-4 text-[var(--primary)]" />
                      Auto-create certification badge for this position
                    </label>
                  </div>
                  {postingForm.create_badge && (
                    <div className="flex items-center gap-2 pl-6">
                      <Label className="text-sm">Badge Color:</Label>
                      <div className="flex gap-2">
                        {['blue', 'green', 'purple', 'amber', 'red', 'teal'].map(color => (
                          <button
                            key={color}
                            type="button"
                            onClick={() => setPostingForm({...postingForm, badge_color: color})}
                            className={`w-6 h-6 rounded-full border-2 transition-all ${
                              postingForm.badge_color === color ? 'border-gray-800 scale-110' : 'border-transparent'
                            }`}
                            style={{ backgroundColor: 
                              color === 'blue' ? '#3b82f6' : 
                              color === 'green' ? '#22c55e' : 
                              color === 'purple' ? '#a855f7' : 
                              color === 'amber' ? '#f59e0b' : 
                              color === 'red' ? '#ef4444' : '#14b8a6'
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="sticky bottom-0 bg-[var(--background-paper)] border-t p-4 flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowPostingModal(false)}>Cancel</Button>
                <Button onClick={handleCreatePosting} className="btn-primary">
                  {isEditing ? 'Update Posting' : 'Create Posting'}
                </Button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Application Detail Modal */}
      <AnimatePresence>
        {showApplicationModal && selectedApplication && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setShowApplicationModal(false)}>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-[var(--background-paper)] rounded-xl w-full max-w-2xl max-h-[90vh] overflow-hidden"
              onClick={e => e.stopPropagation()}
            >
              <div className="sticky top-0 bg-[var(--background-paper)] border-b p-4 flex items-center justify-between z-10">
                <div>
                  <h3 className="text-xl font-bold">{selectedApplication.first_name} {selectedApplication.last_name}</h3>
                  <p className="text-sm text-[var(--text-muted)]">Applied for: {selectedApplication.job_title}</p>
                </div>
                <Button variant="ghost" size="icon" onClick={() => setShowApplicationModal(false)}>
                  <X className="w-5 h-5" />
                </Button>
              </div>
              <div className="p-6 space-y-6 overflow-y-auto max-h-[calc(90vh-200px)]">
                {/* Status */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">Status:</span>
                    {getStatusBadge(selectedApplication.status, 'application')}
                  </div>
                  <Select 
                    value={selectedApplication.status} 
                    onValueChange={v => handleUpdateApplicationStatus(selectedApplication.id, v)}
                  >
                    <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {APPLICATION_STATUSES.map(s => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Contact Info */}
                <div className="card p-4 bg-[var(--accent)]">
                  <h4 className="font-medium mb-3">Contact Information</h4>
                  <div className="grid md:grid-cols-2 gap-3 text-sm">
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4 text-[var(--text-muted)]" />
                      <a href={`mailto:${selectedApplication.email}`} className="text-[var(--primary)] hover:underline">
                        {selectedApplication.email}
                      </a>
                    </div>
                    <div className="flex items-center gap-2">
                      <Phone className="w-4 h-4 text-[var(--text-muted)]" />
                      <a href={`tel:${selectedApplication.phone}`} className="text-[var(--primary)] hover:underline">
                        {selectedApplication.phone}
                      </a>
                    </div>
                    {selectedApplication.address && (
                      <div className="flex items-center gap-2 md:col-span-2">
                        <MapPin className="w-4 h-4 text-[var(--text-muted)]" />
                        {selectedApplication.address}, {selectedApplication.city}, {selectedApplication.state} {selectedApplication.zip_code}
                      </div>
                    )}
                    {selectedApplication.linkedin_url && (
                      <div className="flex items-center gap-2">
                        <ExternalLink className="w-4 h-4 text-[var(--text-muted)]" />
                        <a href={selectedApplication.linkedin_url} target="_blank" rel="noopener noreferrer" className="text-[var(--primary)] hover:underline">
                          LinkedIn Profile
                        </a>
                      </div>
                    )}
                  </div>
                </div>

                {/* Details */}
                <div className="space-y-4">
                  {selectedApplication.availability && (
                    <div>
                      <h4 className="font-medium text-sm text-[var(--text-muted)]">Availability</h4>
                      <p>{selectedApplication.availability}</p>
                    </div>
                  )}
                  {selectedApplication.desired_salary && (
                    <div>
                      <h4 className="font-medium text-sm text-[var(--text-muted)]">Desired Salary</h4>
                      <p>{selectedApplication.desired_salary}</p>
                    </div>
                  )}
                  {selectedApplication.start_date && (
                    <div>
                      <h4 className="font-medium text-sm text-[var(--text-muted)]">Available Start Date</h4>
                      <p>{selectedApplication.start_date}</p>
                    </div>
                  )}
                  {selectedApplication.how_heard && (
                    <div>
                      <h4 className="font-medium text-sm text-[var(--text-muted)]">How They Heard About Us</h4>
                      <p>{selectedApplication.how_heard}</p>
                    </div>
                  )}
                  {selectedApplication.cover_letter && (
                    <div>
                      <h4 className="font-medium text-sm text-[var(--text-muted)]">Cover Letter</h4>
                      <p className="whitespace-pre-wrap bg-[var(--background-subtle)] p-3 rounded-lg text-sm">{selectedApplication.cover_letter}</p>
                    </div>
                  )}
                  {selectedApplication.additional_info && (
                    <div>
                      <h4 className="font-medium text-sm text-[var(--text-muted)]">Additional Information</h4>
                      <p className="whitespace-pre-wrap">{selectedApplication.additional_info}</p>
                    </div>
                  )}
                  {selectedApplication.resume_url && (
                    <div>
                      <h4 className="font-medium text-sm text-[var(--text-muted)]">Resume</h4>
                      <a 
                        href={selectedApplication.resume_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-[var(--primary)] hover:underline"
                      >
                        <FileText className="w-4 h-4" /> View Resume
                      </a>
                    </div>
                  )}
                  {selectedApplication.notes && (
                    <div>
                      <h4 className="font-medium text-sm text-[var(--text-muted)]">Internal Notes</h4>
                      <p className="whitespace-pre-wrap bg-[var(--warning)]/10 p-3 rounded-lg text-sm">{selectedApplication.notes}</p>
                    </div>
                  )}
                </div>
              </div>
              <div className="sticky bottom-0 bg-[var(--background-paper)] border-t p-4 flex justify-between">
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    className="text-[var(--error)] border-[var(--error)]/30"
                    onClick={() => handleArchiveApplication(selectedApplication.id)}
                  >
                    <Archive className="w-4 h-4 mr-2" /> Archive
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => handlePrintApplication(selectedApplication)}
                  >
                    <Printer className="w-4 h-4 mr-2" /> Print
                  </Button>
                </div>
                <div className="flex gap-2">
                  {selectedApplication.status === 'submitted' && (
                    <Button 
                      onClick={() => handleUpdateApplicationStatus(selectedApplication.id, 'under_review')}
                      className="btn-primary"
                    >
                      Start Review
                    </Button>
                  )}
                  {selectedApplication.status === 'under_review' && (
                    <>
                      <Button 
                        variant="outline"
                        onClick={() => handleUpdateApplicationStatus(selectedApplication.id, 'rejected')}
                      >
                        <XCircle className="w-4 h-4 mr-2" /> Reject
                      </Button>
                      <Button 
                        onClick={() => handleUpdateApplicationStatus(selectedApplication.id, 'interview_scheduled')}
                        className="btn-primary"
                      >
                        <Calendar className="w-4 h-4 mr-2" /> Schedule Interview
                      </Button>
                    </>
                  )}
                  {selectedApplication.status === 'interviewed' && (
                    <>
                      <Button 
                        variant="outline"
                        onClick={() => handleUpdateApplicationStatus(selectedApplication.id, 'rejected')}
                      >
                        <XCircle className="w-4 h-4 mr-2" /> Reject
                      </Button>
                      <Button 
                        onClick={() => handleUpdateApplicationStatus(selectedApplication.id, 'offer_extended')}
                        className="btn-primary"
                      >
                        Extend Offer
                      </Button>
                    </>
                  )}
                  {selectedApplication.status === 'offer_extended' && (
                    <Button 
                      onClick={() => handleUpdateApplicationStatus(selectedApplication.id, 'hired')}
                      className="btn-primary bg-[var(--success)] hover:bg-[var(--success)]/80"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" /> Mark as Hired
                    </Button>
                  )}
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Mobile Action Bar - Post Job */}
      <MobileActionBar
        actions={[
          {
            id: 'new-posting',
            label: 'Post New Job',
            icon: 'Plus',
            variant: 'success',
            onClick: () => {
              resetPostingForm();
              setIsEditing(false);
              setShowPostingModal(true);
            }
          }
        ]}
      />
    </div>
  );
};

export default CareersTab;
